import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, idr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PageHeader, StatusBadge } from "@/components/Common";
import { RatingSummary } from "@/components/RatingSummary";
import { MarginReport } from "@/components/MarginReport";
import { BotStatus } from "@/components/BotStatus";
import { DataCheck } from "@/components/DataCheck";
import { FileText, ReceiptText, Package, TrendingUp, Bot, ArrowUpRight, Trophy, XCircle, CalendarIcon, X } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { format, startOfMonth, endOfMonth, subDays, startOfYear } from "date-fns";
import { id as idLocale } from "date-fns/locale";

const toYmd = (d) => (d ? format(d, "yyyy-MM-dd") : "");
const fromYmd = (s) => (s ? new Date(`${s}T00:00:00`) : undefined);

const PRESETS = [
  { key: "today", label: "Hari ini", range: () => { const t = new Date(); return [t, t]; } },
  { key: "7d", label: "7 hari", range: () => [subDays(new Date(), 6), new Date()] },
  { key: "30d", label: "30 hari", range: () => [subDays(new Date(), 29), new Date()] },
  { key: "month", label: "Bulan ini", range: () => [startOfMonth(new Date()), endOfMonth(new Date())] },
  { key: "year", label: "Tahun ini", range: () => [startOfYear(new Date()), new Date()] },
];

function DateField({ testid, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const selected = fromYmd(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={testid}
          variant="outline"
          className="w-40 justify-start rounded-sm bg-card border-border font-normal text-sm"
        >
          <CalendarIcon className="w-4 h-4 mr-2 text-muted-foreground shrink-0" />
          {selected ? (
            <span className="flex-1 text-left">{format(selected, "d MMM yyyy", { locale: idLocale })}</span>
          ) : (
            <span className="flex-1 text-left text-muted-foreground">{placeholder}</span>
          )}
          {value && (
            <X
              className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive"
              data-testid={`${testid}-clear`}
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-sm" align="start">
        <Calendar
          mode="single"
          locale={idLocale}
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => { onChange(toYmd(d)); setOpen(false); }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-border bg-card p-6 hover:-translate-y-px transition-transform duration-150 rounded-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="overline text-muted-foreground">{label}</div>
        <Icon className={`w-4 h-4 ${accent ? "text-accent" : "text-primary"}`} />
      </div>
      <div className="font-heading font-black text-2xl sm:text-3xl tracking-tight">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    const qs = [];
    if (dateFrom) qs.push(`start=${dateFrom}`);
    if (dateTo) qs.push(`end=${dateTo}`);
    api.get(`/dashboard/stats${qs.length ? `?${qs.join("&")}` : ""}`).then((r) => setStats(r.data)).catch(() => {});
  }, [dateFrom, dateTo]);

  if (!stats)
    return (
      <div>
        <PageHeader overline="Ikhtisar" title="Dashboard" />
        <div className="p-8 text-muted-foreground font-mono text-sm">Memuat…</div>
      </div>
    );

  return (
    <div data-testid="dashboard-page">
      <PageHeader overline="Ikhtisar Bisnis" title="Dashboard">
        <BotStatus />
        <div className="flex items-center gap-1 mr-1" data-testid="date-presets">
          {PRESETS.map((p) => {
            const [s, e] = p.range();
            const active = dateFrom === toYmd(s) && dateTo === toYmd(e);
            return (
              <button
                key={p.key}
                data-testid={`preset-${p.key}`}
                onClick={() => { setDateFrom(toYmd(s)); setDateTo(toYmd(e)); }}
                className={`px-2.5 py-1.5 rounded-sm text-xs font-medium transition-colors ${
                  active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <DateField testid="dash-date-from" value={dateFrom} onChange={setDateFrom} placeholder="Dari tanggal" />
        <span className="text-muted-foreground text-sm">s/d</span>
        <DateField testid="dash-date-to" value={dateTo} onChange={setDateTo} placeholder="Sampai tanggal" />
        <Link
          to="/ai"
          data-testid="dashboard-ai-cta"
          className="flex items-center gap-2 bg-accent text-accent-foreground px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <Bot className="w-4 h-4" /> Buat Penawaran (AI)
        </Link>
      </PageHeader>

      <div className="p-8 space-y-8">
        <DataCheck />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Stat icon={ReceiptText} label="Total Invoice" value={stats.total_invoices} />
          <Stat icon={TrendingUp} label="Total Revenue" value={idr(stats.revenue)} accent />
          <Stat icon={Package} label="Produk Katalog" value={stats.total_products} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 border border-border bg-card rounded-sm p-6" data-testid="revenue-chart">
            <div className="overline text-muted-foreground mb-4">Omzet Bulanan (dari Invoice)</div>
            {(stats.monthly_revenue || []).length === 0 ? (
              <div className="text-sm text-muted-foreground py-10 text-center">Belum ada data invoice.</div>
            ) : (
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} initialDimension={{ width: 600, height: 260 }} debounce={1}>
                  <BarChart data={stats.monthly_revenue} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 22% 88%)" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(218 12% 46%)" }} />
                    <YAxis tickFormatter={(v) => (v / 1000000).toFixed(0) + "jt"} tick={{ fontSize: 11, fill: "hsl(218 12% 46%)" }} width={44} />
                    <Tooltip formatter={(v) => idr(v)} cursor={{ fill: "hsl(210 24% 96%)" }} contentStyle={{ borderRadius: 4, fontSize: 12 }} />
                    <Bar dataKey="total" fill="#2563EB" radius={[3, 3, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="border border-border bg-card rounded-sm p-6" data-testid="winloss-card">
            <div className="overline text-muted-foreground mb-4">Penawaran Menang / Kalah</div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-sm"><Trophy className="w-4 h-4 text-emerald-600" /> Menang</span>
                <span className="font-heading font-black text-2xl text-emerald-600">{stats.win_loss?.won ?? 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-sm"><XCircle className="w-4 h-4 text-destructive" /> Kalah</span>
                <span className="font-heading font-black text-2xl text-destructive">{stats.win_loss?.lost ?? 0}</span>
              </div>
              <div className="flex items-center justify-between text-muted-foreground">
                <span className="text-sm">Dalam proses</span>
                <span className="font-mono">{stats.win_loss?.pending ?? 0}</span>
              </div>
              {(() => {
                const w = stats.win_loss?.won ?? 0, l = stats.win_loss?.lost ?? 0, t = w + l;
                const pct = t ? Math.round((w / t) * 100) : 0;
                return (
                  <div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden flex">
                      <div className="bg-emerald-500 h-full" style={{ width: `${pct}%` }} />
                      <div className="bg-destructive h-full" style={{ width: `${100 - pct}%` }} />
                    </div>
                    <div className="overline text-muted-foreground mt-2">Win rate: {pct}%</div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 border border-border bg-card rounded-sm">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div className="overline text-muted-foreground">Penawaran Terbaru</div>
              <Link to="/quotations" className="text-xs text-primary hover:underline flex items-center gap-1">
                Lihat semua <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="divide-y divide-border">
              {stats.recent_quotations.length === 0 && (
                <div className="px-6 py-8 text-muted-foreground text-sm">Belum ada penawaran.</div>
              )}
              {stats.recent_quotations.map((q) => (
                <Link
                  key={q.id}
                  to={`/quotations/${q.id}`}
                  className="px-6 py-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
                >
                  <div>
                    <div className="font-mono text-xs text-muted-foreground">{q.quote_number}</div>
                    <div className="font-medium text-sm">{q.customer_name}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-sm">{idr(q.total)}</span>
                    <StatusBadge status={q.status} />
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="border border-border bg-card rounded-sm">
            <div className="px-6 py-4 border-b border-border">
              <div className="overline text-muted-foreground">Status Penawaran</div>
            </div>
            <div className="p-6 space-y-3">
              {Object.keys(stats.by_status).length === 0 && (
                <div className="text-muted-foreground text-sm">Belum ada data.</div>
              )}
              {Object.entries(stats.by_status).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <StatusBadge status={k} />
                  <span className="font-mono text-sm">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <MarginReport />
        <RatingSummary />
      </div>
    </div>
  );
}
